/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {RefObject, useEffect, useState} from 'react';

export interface MapBottomRightOffset {
  top: number;
  bottom: number;
  right: number;
}

/**
 * Provides a hook which measures the live distance, in pixels, from the
 * viewport's edges to a target element's own edges - the given ESRI
 * `MapView`'s own container by default, or an arbitrary DOM element (e.g.
 * a map widget's own trigger button) when `measureRef` is supplied.
 *
 * This is used to position portal-based map widget panels relative to a
 * live element's own corner rather than in-tree, since ancestor containers
 * (e.g. a resizable grid container) may resize the map area - and so move
 * the measured element - without the window itself being resized. A
 * `ResizeObserver` on the map's own container catches that case, unlike a
 * plain window resize listener - it stays the observed target even when
 * `measureRef` points elsewhere, since it's the element whose resize
 * actually signals that a re-measurement is needed.
 *
 * ```typescript
 *
 *   import {useMapBottomRightOffset} from
 *     '@ion/features/src/maps/hooks/useMapBottomRightOffset';
 *
 *   const offset = useMapBottomRightOffset(view);
 *
 *   // measure a different element (e.g. a trigger button) instead of the
 *   // map's own container, while still re-measuring on the map's resize ...
 *   const buttonOffset = useMapBottomRightOffset(view, buttonRef);
 *
 * ```
 *
 * @param   view The ESRI `MapView` whose own container is watched for
 *          resize, and measured by default when `measureRef` is omitted.
 * @param   measureRef When provided, this element's own edges are measured
 *          instead of the map view's container.
 * @returns The live distance, in pixels, from the viewport's top, bottom,
 *          and right edges to the measured element's own edges, or `null`
 *          until the first measurement completes.
 */
export const useMapBottomRightOffset = (
  view: __esri.MapView,
  measureRef?: RefObject<HTMLElement>
): MapBottomRightOffset | null => {
  const [offset, setOffset] = useState<MapBottomRightOffset | null>(null);

  useEffect(() => {
    const updateOffset = function() {
      const rect = (measureRef?.current ?? view?.container)?.getBoundingClientRect?.();
      if (rect) {
        setOffset({
          top    : rect.top,
          bottom : Math.max(window.innerHeight - rect.bottom, 0),
          right  : Math.max(window.innerWidth - rect.right, 0)
        });
      }
    };

    updateOffset();

    const target = view?.container;
    const observer = target ? new ResizeObserver(updateOffset) : null;
    observer?.observe(target);
    window.addEventListener('resize', updateOffset);

    return function() {
      observer?.disconnect();
      window.removeEventListener('resize', updateOffset);
    };
  }, [view, measureRef]);

  return offset;
};

export default useMapBottomRightOffset;
